import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import { readBootstrapState } from "../../lib/bootstrap-state.js";
import { readStatusBlob, type StatusDoc } from "../../lib/bootstrap-status.js";
import { getAciState } from "../../lib/bootstrap-aci-state.js";
import { finalizeInstall } from "../../lib/bootstrap-finalize.js";
import { InstallEventEmitter } from "../../lib/install-events.js";

export class BootstrapStatusCommand extends M8tCommand {
  // `bootstrap finish` is a DEPRECATED ALIAS, not a second command. Everything it
  // used to do either moved into the cloud installer (the founder's data-plane
  // grant, now at entrypoint.sh step 3b) or into this command's own `done`
  // handling. The path stays registered because it is written down in guides,
  // runbooks and shakedown recipes that a founder may already be part-way
  // through — it prints a deprecation notice and does the right thing.
  static paths = [["bootstrap", "status"], ["bootstrap", "finish"]];
  static usage = Command.Usage({
    description: "Show the cloud installer's live status (phase, progress, result) — and finish the local setup when it lands.",
    details:
      "Reads the durable status blob written by the installer. --watch polls until the install reaches done or failed.\n\n" +
      "On reaching done under --watch this also completes the local half of the install, which nothing else in the bootstrap path can do: it registers the webapp's sign-in redirect URI (the installer runs as a managed identity with no directory role, and at launch time the gateway FQDN did not exist yet), writes ~/.m8t/repo-root and the gateway discovery cache, and seeds your advisors' brains from the onboarding intake. Re-runnable — `m8t bootstrap finish` is a deprecated alias that does exactly this on an already-done install.",
    examples: [
      ["One read", "$0 bootstrap status"],
      ["Watch to completion, then finish the local setup", "$0 bootstrap status --watch"],
      ["Redo the local setup on a finished install", "$0 bootstrap status --finalize --repo-root /path/to/m8t"],
    ],
  });

  watch = Option.Boolean("--watch", false);
  output = Option.String("--output");
  repoRoot = Option.String("--repo-root", { description: "The m8t clone to point local tools at (default: the current directory)." });
  finalize = Option.Boolean("--finalize", false, { description: "Complete the local setup against an already-done install, without watching." });
  subscription = Option.String("--subscription");
  resourceGroup = Option.String("--resource-group");

  async executeCommand(): Promise<number> {
    const state = await readBootstrapState();
    if (!state) {
      throw new LocalCliError({
        code: "BOOTSTRAP_NO_STATE",
        message: "No bootstrap is in flight (no ~/.m8t/bootstrap.json).",
        hint: "Run 'm8t bootstrap launch --location <region>' first.",
      });
    }
    // Standard convention: auto → pretty on a TTY, json when piped/redirected
    // (matches every other m8t command). No bespoke default.
    const mode = resolveOutputMode(
      typeof this.output === "string" ? (this.output as "pretty" | "json" | "auto") : undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
    const watch = this.watch === true;
    // An install launched before this CLI recorded install ids cannot be
    // correlated, and the contract requires the id on every event from launch
    // onward. Fabricating one is not an option — a plausible-looking value that
    // joins to nothing is worse than none, because a consumer will join on it
    // and silently get back nothing.
    //
    // But WHO asked matters. `--output json` resolves from `auto` whenever
    // stdout is a pipe, and the paved path runs this command inside a coding
    // agent whose tool IS a pipe. Refusing on that resolution would hard-stop a
    // founder who launched with an older CLI, upgraded, and then ran exactly the
    // documented command — for the sake of a correlation id they never asked
    // for. So an explicit `--output json` refuses (the caller asked for a
    // machine stream and cannot be given a valid one), and an inferred one
    // quietly falls back to prose.
    const askedForJson = this.output === "json";
    let streamable = watch && mode === "json";
    if (streamable && state.installId === undefined) {
      if (askedForJson) {
        throw new LocalCliError({
          code: "BOOTSTRAP_NO_INSTALL_ID",
          message: "This install was launched by a CLI that did not record an install id, so its events cannot be correlated.",
          hint: "Watch it as prose instead: m8t bootstrap status --watch --output pretty",
        });
      }
      this.context.stderr.write(
        `  ${colors.dim("note: this install predates install-id recording, so no event stream is available — showing progress as text.")}\n`,
      );
      streamable = false;
    }
    // Events stream only while WATCHING. A plain `status --output json` is a
    // single read and keeps emitting a single status document, which is what it
    // has always done and what existing consumers parse.
    const events = new InstallEventEmitter({
      command: "bootstrap.status",
      mode: streamable ? "json" : "pretty",
      write: (line) => this.context.stdout.write(line),
      ...(state.installId !== undefined ? { installId: state.installId } : {}),
    });
    // Cloud phases already reported, so a re-read does not replay them; and
    // warnings already surfaced, so an accumulating array is not re-emitted on
    // every poll.
    const seenPhases = new Set<string>();
    const seenWarnings = new Set<string>();
    // `this.path` is the path clipanion actually matched, so the alias can
    // announce itself without a second command class. clipanion only assigns it
    // at dispatch, so it is genuinely absent under a direct instantiation (the
    // unit-test harness) — read it defensively rather than trusting the type.
    const invokedPath = (this.path as string[] | undefined) ?? [];
    const viaFinishAlias = invokedPath[invokedPath.length - 1] === "finish";
    if (viaFinishAlias) {
      this.context.stderr.write(
        `  ${colors.hint("note:")} ${colors.dim("`m8t bootstrap finish` is deprecated — `m8t bootstrap status --watch` now finishes the local setup itself when the install lands.")}\n`,
      );
    }
    // A bare `m8t bootstrap status` is a peek and must stay read-only: it is what
    // an operator runs to look at someone else's in-flight install. Finalizing
    // writes to the directory and to ~/.m8t, so it is gated on asking for it —
    // by watching the install to completion, by the deprecated alias, or by
    // --finalize on an install that is already done.
    const wantsFinalize = watch || viaFinishAlias || this.finalize === true;

    for (;;) {
      let doc: StatusDoc;
      try {
        doc = await readStatusBlob({ saName: state.statusSaName, resourceGroup: state.resourceGroup, subscriptionId: state.subscriptionId });
      } catch (e) {
        // The blob doesn't exist until the installer's phase 1 (status storage),
        // which lands a minute or so after the ACI cold-starts. In --watch we
        // tolerate that startup window and keep polling; a single read still errors.
        if (watch && e instanceof LocalCliError && e.code === "BOOTSTRAP_STATUS_UNREADABLE") {
          // ...but an abort BEFORE phase 1 - a missing required variable, say -
          // dies with no storage account to publish to, so no blob will ever
          // appear. A terminated container is the only evidence available on that
          // path; without this check the loop waits forever on a run that is over.
          const early = await getAciState({
            aciName: state.aciName, resourceGroup: state.resourceGroup, subscriptionId: state.subscriptionId,
          }).catch(() => null);
          if (early?.terminated) {
            this.context.stderr.write(
              `  ${colors.error("✗")} the installer container has terminated (exit ${String(early.exitCode ?? "?")}) and never reported a status.\n` +
              `  ${colors.hint("inspect:")} az container logs -n ${state.aciName} -g ${state.resourceGroup}\n` +
              `  ${colors.hint("reap:")} m8t bootstrap reap --force\n`,
            );
            // A stream that simply stops is indistinguishable from one still
            // running, and this is the likeliest real failure a wrapper needs to
            // hear about.
            events.failed("install", "BOOTSTRAP_CONTAINER_TERMINATED",
              "the installer container terminated before reporting any status",
              { exitCode: early.exitCode ?? null });
            return 1;
          }
          if (mode === "pretty") this.context.stderr.write(`  ${colors.dim("waiting for the installer to start…")}\n`);
          await sleep(10_000);
          continue;
        }
        events.failed("install", e instanceof LocalCliError ? e.code : "BOOTSTRAP_STATUS_UNREADABLE", (e as Error).message);
        throw e;
      }
      if (!watch || doc.status !== "running") {
        relay(events, doc, seenPhases, seenWarnings);
        // In a watched json run stdout is an event STREAM, so the whole document
        // rides in the terminal event's data rather than being written raw —
        // two shapes on one stream is exactly what a wrapper cannot parse.
        if (mode === "json" && !watch) { this.context.stdout.write(renderJson(doc) + "\n"); }
        else if (!streamable) { this.context.stdout.write(formatStatus(doc)); }
        if (wantsFinalize) {
          if (doc.status === "done") {
            await this.runFinalize(doc, state.subscriptionId, state.resourceGroup, mode, events);
          } else if (this.finalize === true || viaFinishAlias) {
            // Asked for explicitly against an install that is not done. The old
            // `finish` refused here rather than half-configuring a machine
            // against a platform that may not exist; keep that refusal.
            events.failed("install", "BOOTSTRAP_FINISH_NOT_DONE",
              `the platform is not live yet (status: ${doc.status}, phase: ${doc.phase})`);
            throw new LocalCliError({
              code: "BOOTSTRAP_FINISH_NOT_DONE",
              message: `The platform is not live yet (status: ${doc.status}, phase: ${doc.phase}).`,
              hint: "Wait for 'm8t bootstrap status --watch' to reach done — it finishes the local setup itself.",
            });
          }
        }
        // The terminal event, emitted AFTER finalize so it reflects the whole
        // install rather than only its cloud half. Finalize is what registers the
        // sign-in redirect URI: a wrapper told "done" before it runs is being
        // told about a platform the founder may not be able to sign in to.
        if (doc.status === "failed") {
          // `code` is a STABLE LOCAL code, never the cloud phase slug. A
          // consumer switching on `code` must not have to handle two disjoint
          // vocabularies depending on whether the failure was local or cloud;
          // the phase travels in `data` where it belongs.
          events.failed(
            "install", "BOOTSTRAP_INSTALL_FAILED",
            doc.error?.message ?? "the installer reported a failure",
            { doc: forWire(doc), phase: doc.error?.phase ?? doc.phase },
          );
        } else {
          events.ok("install", "the platform is live", { doc: forWire(doc) });
        }
        return doc.status === "failed" ? 1 : 0;
      }
      relay(events, doc, seenPhases, seenWarnings);
      if (mode === "pretty") this.context.stderr.write(`  ${colors.dim(`${doc.phase} (${String(doc.phaseIndex)}/${String(doc.phaseTotal)}) ${doc.detail ?? ""}`)}\n`);
      const aci = await getAciState({ aciName: state.aciName, resourceGroup: state.resourceGroup, subscriptionId: state.subscriptionId }).catch(() => null);
      if (aci?.terminated) {
        this.context.stderr.write(
          `  ${colors.error("✗")} the installer container has terminated (exit ${String(aci.exitCode ?? "?")}) but the status is still '${doc.phase}'.\n` +
          `  ${colors.hint("inspect:")} az container logs -n ${state.aciName} -g ${state.resourceGroup}\n` +
          `  ${colors.hint("reap:")} m8t bootstrap reap --force\n`,
        );
        events.failed("install", "BOOTSTRAP_CONTAINER_TERMINATED",
          `the installer container terminated while the status still read '${doc.phase}'`,
          { exitCode: aci.exitCode ?? null, phase: doc.phase });
        return 1;
      }
      await sleep(10_000);
    }
  }

  /**
   * Complete the local half of the install. Never throws: the platform is live,
   * and a failure to point one machine at it must not turn a successful install
   * into a non-zero exit — every step inside reports its own failure with the
   * remedy, and the outer catch covers the ones that cannot.
   *
   * In `--output json` mode stdout carries the status document and nothing else,
   * so all of finalize's prose is routed to stderr rather than corrupting it.
   */
  private async runFinalize(
    doc: StatusDoc, subscriptionId: string, resourceGroup: string, mode: "pretty" | "json",
    events?: InstallEventEmitter,
  ): Promise<void> {
    const toStderr = (s: string) => this.context.stderr.write(s);
    events?.running("finalize", "completing the local half of the install");
    try {
      await finalizeInstall({
        doc,
        subscriptionId: (typeof this.subscription === "string" ? this.subscription : undefined) ?? subscriptionId,
        resourceGroup: (typeof this.resourceGroup === "string" ? this.resourceGroup : undefined) ?? resourceGroup,
        ...(typeof this.repoRoot === "string" ? { repoRoot: this.repoRoot } : {}),
        stdout: mode === "json" ? toStderr : (s) => this.context.stdout.write(s),
        stderr: toStderr,
      });
      events?.ok("finalize", "the local half of the install is complete");
    } catch (e) {
      this.context.stderr.write(
        `  ${colors.error("⚠ the platform is live, but the local setup did not complete:")} ${(e as Error).message}\n` +
        `  ${colors.hint("retry:")} m8t bootstrap status --finalize\n`,
      );
      // Reported as a FAILED step, not swallowed into the terminal `ok`. The
      // platform really is live, so the command still exits 0 — but a wrapper
      // that renders "all done" without knowing the redirect URI was never
      // registered is setting the founder up for AADSTS50011 at first sign-in.
      events?.failed("finalize", "BOOTSTRAP_FINALIZE_INCOMPLETE", (e as Error).message);
    }
  }
}

/**
 * The status document, minus the one field that must not leave this machine.
 *
 * `error.message` is free-form and may carry raw `az` stderr — the contract
 * declares it LOCAL ONLY for that reason. Embedding the whole document in a
 * terminal event would hand it to exactly the audience the rule excludes: a
 * program, which may well ship it onward. The founder still sees it, on their
 * own terminal, as the event's own `message`.
 */
export function forWire(doc: StatusDoc): StatusDoc {
  if (doc.error?.message === undefined) return doc;
  const { message: _local, ...rest } = doc.error;
  return { ...doc, error: rest };
}

/**
 * Turn what the status document says into events, without repeating itself.
 *
 * The document is CURRENT STATE and is re-read every poll, so everything in it
 * is present on every read. Emitting it verbatim each time would produce a
 * stream that says the same thing forty times; the two `seen` sets are what make
 * a poll loop into a sequence of changes.
 *
 * Warnings become `running` + `warn` events. That is the pairing the envelope's
 * free-level case exists for, and it is the first time the installer's
 * `warnings` array reaches a human or a program at all — it has been written by
 * the installer, declared by the contract, and rendered nowhere.
 */
export function relay(
  events: { active: boolean; running: (s: string, m?: string, d?: Record<string, unknown>) => void; warn: (s: string, m: string, d?: Record<string, unknown>) => void },
  doc: StatusDoc,
  seenPhases: Set<string>,
  seenWarnings: Set<string>,
): void {
  if (!events.active) return;
  for (const w of doc.warnings ?? []) {
    const key = `${w.phase}|${w.at}|${w.text}`;
    if (seenWarnings.has(key)) continue;
    seenWarnings.add(key);
    events.warn(w.phase, w.text, { at: w.at });
  }
  const key = `${doc.phase}|${String(doc.phaseIndex)}`;
  if (!seenPhases.has(key)) {
    seenPhases.add(key);
    events.running(doc.phase, doc.detail === "" ? undefined : doc.detail, {
      phaseIndex: doc.phaseIndex,
      phaseTotal: doc.phaseTotal,
    });
  }
}

/** Exported for tests — pure rendering, no I/O. */
export function formatStatus(d: StatusDoc): string {
  const head = `${d.status.toUpperCase()} — ${d.phase} (${String(d.phaseIndex)}/${String(d.phaseTotal)})${d.detail ? ` — ${d.detail}` : ""}\n`;
  if (d.status === "done" && d.result) {
    return colors.success("✓ ") + head +
      `  gateway:  ${d.result.gatewayUrl ?? "-"}\n` +
      `  foundry:  ${d.result.foundryEndpoint ?? "-"}\n` +
      `  ${colors.hint("next:")} m8t bootstrap reap\n`;
  }
  if (d.status === "failed" && d.error) {
    // Both fields are optional because the installer's exit trap can publish a
    // bare `{}` when it cannot compose them — the fallback that guarantees a
    // dead install still reports `failed` instead of freezing on `running`.
    // Say so plainly rather than rendering "error in undefined: undefined";
    // the phase is still on the document's own `phase` field either way.
    const where = d.error.phase ?? d.phase;
    const why = d.error.message ?? "the installer did not record why — check the container logs";
    return colors.error("✗ ") + head + `  ${colors.error(`error in ${where}: ${why}`)}\n  ${colors.hint("inspect:")} az container logs\n`;
  }
  return head;
}
